import type { ReactNode } from 'react';
import { EmergeMark } from '@/components/EmergeMark';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PageHeader,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { ExternalTextLink } from '@/components/ui/TextLink';
import { usePageLayout } from '@/lib/use-page-layout';

/*
 * /brand — the mark, the palette, the type, and the rules for using them.
 *
 * ⛔ THIS IS NOT AN EXCEPTION TO THE NO-HELPER-TEXT PROHIBITION, AND A SWEEP
 * MUST NOT GUT IT. That rule forbids prose attached to a CONTROL, where the
 * sentence competes with the control, is read by people who did not want it,
 * and rots silently against the thing it sits beside. This is a DESTINATION
 * somebody opens on purpose — nothing here renders next to a field, and
 * removing it removes a page, not a caption. Same standing as `GuideDrawer`.
 *
 * EVERY COLOUR ON THIS PAGE COMES FROM A TOKEN, including the swatches. The hex
 * values are printed as DATA — they are the thing being documented — but no
 * element is painted from one, so light and dark still swap in `index.css`
 * alone and no `dark:` variant appears anywhere below.
 *
 * ⚠️ THE MARK IS DRAWN ONCE, IN `EmergeMark`. The two paths appear again in
 * this file only inside the wordmark and lockup, where the E is SCALED into a
 * line of type and so cannot be that component. If they ever disagree,
 * `EmergeMark` is the original.
 */

/** The lifted arm and the body, at word scale. See `EmergeMark` for the mark itself. */
const BODY = 'M14 25H23V33.5H43.5V41H23V45.5H49.5V53H14Z';
const ARM = 'M22.35 13.12L47.95 8.6L49.26 15.99L23.65 20.5Z';

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Panel>
      <PanelHeader title={title} description={description} />
      <PanelBody>{children}</PanelBody>
    </Panel>
  );
}

/* ------------------------------------------------------------------ the mark */

/*
 * ⚠️ THE LABEL COLOUR IS CARRIED PER GROUND, AND IT HAS TO BE.
 *
 * Three of these four grounds are FIXED in both themes, so theme-aware text on
 * them is a bug that only appears in one theme: `text-fg-muted` on `bg-white`
 * is dark-on-white in the light theme and light-on-white — invisible — in the
 * dark one. The fixed grounds therefore take fixed ink from the seed palette,
 * and only the ground that follows the theme takes text that follows it too.
 * This is the same trap that killed the navy crown in an earlier cut of the
 * mark, arriving on a caption instead of on artwork.
 */
const GROUNDS: readonly {
  bg: string;
  label: string;
  note: string;
  ink: string;
  subInk: string;
}[] = [
  { bg: 'bg-white', label: 'White', note: 'light surface', ink: 'text-seed-navy', subInk: 'text-seed-navy/55' },
  { bg: 'bg-seed-cream', label: 'Cream', note: '#f0e8da', ink: 'text-seed-navy', subInk: 'text-seed-navy/55' },
  { bg: 'bg-seed-navy', label: 'Navy', note: '#1a1f4e', ink: 'text-seed-cream/75', subInk: 'text-seed-cream/50' },
  {
    bg: 'bg-surface border border-border',
    label: 'App surface',
    note: 'follows your theme',
    ink: 'text-fg-muted',
    subInk: 'text-fg-subtle',
  },
];

function MarkSection() {
  return (
    <Section
      title="An E with its top arm lifted off"
      description="The primary mark, and the one colour it is drawn in."
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-10">
        <div className="grid h-40 w-40 shrink-0 place-items-center rounded-lg border border-border bg-surface-sunken">
          <EmergeMark className="h-28 w-28" />
        </div>
        <div className="flex min-w-0 flex-col gap-3 text-sm text-fg-secondary">
          <p className="m-0 max-w-[62ch]">
            The letter names the product; the detached arm is the verb. Two paths, one colour,
            and it still reads as an <span className="font-semibold text-fg">E</span> at 16
            pixels — which is where a mark for a sidebar chip and a browser tab has to survive.
          </p>
          <p className="m-0 max-w-[62ch]">
            It is drawn in <span className="font-mono text-xs text-fg">seed-orange</span> on
            every ground, and that is load-bearing rather than a preference. An earlier cut set
            part of the mark in <span className="font-mono text-xs text-fg">seed-navy</span> and
            it vanished against the sign-in screen, whose field <em>is</em> that navy. No
            dark-mode branch fixes it, because that navy is a brand surface in the light theme
            too.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {GROUNDS.map((g) => (
          <div key={g.label} className={`rounded-lg p-5 ${g.bg}`}>
            <div className="grid place-items-center">
              <EmergeMark className="h-14 w-14" />
            </div>
            <p
              className={`m-0 mt-4 text-center text-2xs font-medium uppercase tracking-[0.1em] ${g.ink}`}
            >
              {g.label}
            </p>
            <p className={`m-0 mt-0.5 text-center font-mono text-2xs ${g.subInk}`}>{g.note}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ----------------------------------------------------------------- the scale */

const SIZES = [16, 20, 24, 32, 48, 96] as const;

function ScaleSection() {
  return (
    <Section
      title="It has to survive the sidebar"
      description="16px is the real floor, not a stress test."
    >
      <p className="m-0 max-w-[62ch] text-sm text-fg-secondary">
        The smallest real use is the collapsed sidebar chip — a 28px box with 2px of padding,
        so 24px of mark — and the browser tab below it. Every corner is square or blunter and
        the body is a single path, so nothing thins to a spike when it is rasterised.
      </p>
      <div className="mt-5 flex flex-wrap items-end gap-8 rounded-lg border border-border bg-surface-sunken px-6 py-5">
        {SIZES.map((s) => (
          <div key={s} className="flex flex-col items-center gap-3">
            <EmergeMark style={{ width: s, height: s }} />
            <span className="font-mono text-2xs tabular text-fg-subtle">{s}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------------------------------------------------- the construction */

const SPECS: readonly [string, string][] = [
  ['viewBox', '0 0 64 64'],
  ['Cap height', '28'],
  ['Width : cap', '1.27'],
  ['Arm tilt', '−10°'],
  ['Stroke', '2.2, round join'],
  ['Colour', '#e8632b'],
];

function ConstructionSection() {
  return (
    <Section
      title="Ten degrees off the arm’s rest line"
      description="How the mark is built, and the numbers behind it."
    >
      <div className="overflow-x-auto rounded-lg border border-border bg-surface-sunken p-4">
        <svg
          viewBox="-16 -8 96 80"
          className="mx-auto block h-auto w-full min-w-[340px] max-w-[420px]"
          role="img"
          aria-label="Construction drawing: the arm’s level rest position dashed, the tilted arm solid above the E body, with the ten degree rotation and the gap marked"
        >
          <defs>
            <pattern id="brand-grid" width="8" height="8" patternUnits="userSpaceOnUse">
              <path d="M8 0H0v8" fill="none" className="stroke-border" strokeWidth="0.35" />
            </pattern>
          </defs>
          <rect x="-16" y="-8" width="96" height="80" fill="url(#brand-grid)" />

          <path
            d="M-12 25H72M-12 53H72"
            className="stroke-hairline-strong"
            strokeWidth="0.35"
            strokeDasharray="2 2"
            fill="none"
          />
          {/* Axis labels sit on the RIGHT: on the left they collided with the gap label. */}
          <text x="61" y="24" className="fill-fg-subtle" style={{ fontFamily: 'var(--font-mono)', fontSize: 3.2 }}>
            cap line
          </text>
          <text x="61" y="52" className="fill-fg-subtle" style={{ fontFamily: 'var(--font-mono)', fontSize: 3.2 }}>
            baseline
          </text>

          {/* the arm where it would sit level and attached */}
          <path
            d="M23 13.06H49V20.56H23Z"
            fill="none"
            className="stroke-fg-muted"
            strokeWidth="0.6"
            strokeDasharray="2.5 2"
          />

          <g className="fill-seed-orange stroke-seed-orange" strokeWidth="2.2" strokeLinejoin="round">
            <path d={BODY} />
            <path d={ARM} />
          </g>

          <path d="M53 18.5A7 7 0 0 0 52.3 13.6" fill="none" className="stroke-fg" strokeWidth="0.5" />
          <text x="56.5" y="17.5" className="fill-fg" style={{ fontFamily: 'var(--font-mono)', fontSize: 3.8 }}>
            10°
          </text>

          <path d="M19.5 20.5h-4M19.5 25h-4M17.5 20.5V25" className="stroke-fg" strokeWidth="0.4" fill="none" />
          <text x="1" y="24.2" className="fill-fg" style={{ fontFamily: 'var(--font-mono)', fontSize: 3 }}>
            gap 4.5
          </text>
        </svg>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-3">
        {SPECS.map(([k, v]) => (
          <div key={k} className="bg-surface px-4 py-3">
            <dt className="font-mono text-2xs uppercase tracking-[0.1em] text-fg-muted">{k}</dt>
            <dd className="m-0 mt-1 font-mono text-sm tabular text-fg">{v}</dd>
          </div>
        ))}
      </dl>

      <p className="m-0 mt-5 max-w-[62ch] text-sm text-fg-secondary">
        The arm pivots at its <span className="font-semibold text-fg">left</span> end, not its
        centre — so it reads as peeling off the stem rather than floating in place, and its low
        corner stays clear of the cap line. The rotation is baked into the coordinates rather
        than left as a transform, so nothing depends on a renderer honouring it.
      </p>

      <TableWrap className="mt-4">
        <pre className="m-0 w-max min-w-full px-4 py-3 font-mono text-xs leading-7 text-fg-secondary">
          {`body  d="${BODY}"\narm   d="${ARM}"`}
        </pre>
      </TableWrap>
    </Section>
  );
}

/* -------------------------------------------------------------- the wordmark */

/** The E scaled into a line of 60px type: cap line 49.4, baseline 93. */
const WORD_E = (
  <g
    className="fill-seed-orange stroke-seed-orange"
    strokeWidth="1.413"
    strokeLinejoin="round"
    transform="translate(-21.80 10.47) scale(1.5571)"
  >
    <path d={BODY} />
    <path d={ARM} />
  </g>
);

const SANS = 'var(--font-sans)';

function WordmarkSection() {
  return (
    <Section
      title="The mark is the E of EMERGE"
      description="The wordmark, and the full lockup with the descriptor."
    >
      <p className="m-0 max-w-[62ch] text-sm text-fg-secondary">
        The same drawing does double duty as the word’s first letter. Its body sits on the cap
        line and the baseline like any other letter, and its lifted arm is the only thing that
        breaks above the cap line — so the word does what it says. It uses the mark at{' '}
        <span className="font-semibold text-fg">full display width</span>: a cut proportioned
        like a real E was drawn and rejected, because at letter width the arms shorten and the
        lifted arm reads as a flake rather than a plane.
      </p>

      <div className="mt-5 flex flex-col gap-3">
        <div className="rounded-lg border border-border bg-surface-sunken px-6 py-6">
          <svg viewBox="0 18.9 270 80.1" className="block h-16 w-auto max-w-full" role="img" aria-label="EMERGE wordmark">
            {WORD_E}
            <text
              x="62.28"
              y="93"
              className="fill-fg"
              style={{ fontFamily: SANS, fontSize: 60, fontWeight: 700, letterSpacing: '-0.005em' }}
            >
              MERGE
            </text>
          </svg>
        </div>

        <div className="rounded-lg border border-border bg-surface-sunken px-6 py-6">
          <svg viewBox="0 11 271 112" className="block h-24 w-auto max-w-full" role="img" aria-label="SEED EMERGE, Partnerships and Billing">
            <text
              x="200.42"
              y="42"
              className="fill-fg"
              style={{ fontFamily: SANS, fontSize: 18, fontWeight: 600, letterSpacing: '0.3em' }}
            >
              SEED
            </text>
            {WORD_E}
            <text
              x="62.28"
              y="93"
              className="fill-fg"
              style={{ fontFamily: SANS, fontSize: 60, fontWeight: 700, letterSpacing: '-0.005em' }}
            >
              MERGE
            </text>
            <text
              x="0"
              y="116"
              className="fill-seed-orange"
              style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, letterSpacing: '0.22em' }}
            >
              PARTNERSHIPS &amp; BILLING
            </text>
          </svg>
        </div>
      </div>

      <p className="m-0 mt-5 max-w-[62ch] text-sm text-fg-secondary">
        <span className="font-semibold text-fg">SEED sits on the right because the arm is on
        the left.</span>{' '}
        The lifted arm peaks well above the cap line on the E, so the top-left of the block is
        already occupied; SEED was drawn there first and had to be pushed up to clear it, which
        left it stranded. Moving it back is not a nudge but a collision — and the collision
        happens at SEED’s right end, which is not where anyone looks when repositioning a word
        in a top corner.
      </p>
    </Section>
  );
}

/* --------------------------------------------------------------- the palette */

const BRAND_COLOURS: readonly { name: string; hex: string; swatch: string; use: string }[] = [
  { name: 'seed-navy', hex: '#1a1f4e', swatch: 'bg-seed-navy', use: 'The sign-in field, the theme colour, ink on light grounds.' },
  { name: 'seed-orange', hex: '#e8632b', swatch: 'bg-seed-orange', use: 'The mark, and the descriptor in the lockup. Nothing else.' },
  { name: 'seed-peach', hex: '#f08a5a', swatch: 'bg-seed-peach', use: 'Decoration only.' },
  { name: 'seed-yellow', hex: '#f5c842', swatch: 'bg-seed-yellow', use: 'Decoration only. Too light on white to carry text.' },
  { name: 'seed-cream', hex: '#f0e8da', swatch: 'bg-seed-cream', use: 'Ink on navy, and the plaque behind the mark on the sign-in screen.' },
];

function ColourSection() {
  return (
    <Section
      title="Five brand colours, fixed in both themes"
      description="Decoration and the mark. Not the interactive accent."
    >
      <p className="m-0 max-w-[62ch] text-sm text-fg-secondary">
        These five do not swap with the colour scheme, which is what makes them safe on the
        navy sign-in field and unsafe as body text. They are for{' '}
        <span className="font-semibold text-fg">decoration and the mark</span>. Everything
        interactive — buttons, links, focus rings, active state — uses the theme-aware{' '}
        <span className="font-mono text-xs text-fg">accent-brand</span> family instead, and
        every status colour means status and nothing else.
      </p>

      <div className="mt-5 flex flex-col gap-2">
        {BRAND_COLOURS.map((c) => (
          <div key={c.name} className="flex items-center gap-4 rounded-lg border border-border p-3">
            <div className={`h-12 w-12 shrink-0 rounded-md border border-border ${c.swatch}`} />
            <div className="min-w-0 flex-1">
              <p className="m-0 font-mono text-xs text-fg">{c.name}</p>
              <p className="m-0 mt-0.5 text-xs text-fg-muted">{c.use}</p>
            </div>
            <span className="shrink-0 font-mono text-2xs uppercase tabular text-fg-subtle">
              {c.hex}
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------ the typography */

function TypeSection() {
  return (
    <Section
      title="Three faces, loaded once in index.html"
      description="Two are in daily use; the third is paid for and unspent."
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-border p-5">
          <p className="m-0 font-mono text-2xs uppercase tracking-[0.12em] text-fg-muted">
            font-sans · Inter · everything
          </p>
          <p className="m-0 mt-2 text-2xl font-semibold tracking-[-0.01em] text-fg">
            University partnerships, billing and delivery
          </p>
          <p className="m-0 mt-1 text-sm text-fg-secondary">
            The interface face. 13px base, and the type scale lives in{' '}
            <span className="font-mono text-xs">index.css</span>.
          </p>
        </div>
        <div className="rounded-lg border border-border p-5">
          <p className="m-0 font-mono text-2xs uppercase tracking-[0.12em] text-fg-muted">
            font-mono · JetBrains Mono · figures, ids, code
          </p>
          <p className="m-0 mt-2 font-mono text-xl tabular text-fg">
            123,456.00 GBP · QU-0479 · #e8632b
          </p>
          <p className="m-0 mt-1 text-sm text-fg-secondary">
            Anywhere digits have to line up in a column. Pair it with{' '}
            <span className="font-mono text-xs">tabular-nums</span>.
          </p>
        </div>
        <div className="rounded-lg border border-border p-5">
          <p className="m-0 font-mono text-2xs uppercase tracking-[0.12em] text-fg-muted">
            font-display · Instrument Serif · defined, unused
          </p>
          <p className="m-0 mt-2 font-display text-3xl text-fg">A mark that has to earn its keep</p>
          <p className="m-0 mt-1 text-sm text-fg-secondary">
            The token exists and the face is loaded, but nothing in the app sets it today. It is
            here so the next person choosing a display face finds one already paid for rather
            than adding a fourth.
          </p>
        </div>
      </div>
    </Section>
  );
}

/* ----------------------------------------------------------------- the files */

const FILES: readonly [string, string, string][] = [
  ['emerge-mark.svg', 'seed-orange, fixed', 'Everything. The favicon, social cards, print.'],
  ['emerge-mark-mono.svg', 'currentColor', 'Where the orange cannot be trusted to have contrast: a favicon mask, single-plate print, a muted state.'],
  ['emerge-wordmark.svg', 'orange E, settable ink', 'EMERGE as one word.'],
  ['emerge-lockup.svg', 'orange E, settable ink', 'The full name plus the descriptor.'],
  ['emerge-mark-180.png', 'orange on seed-navy', 'The apple-touch icon. Safari will not take an SVG there.'],
];

function FilesSection() {
  return (
    <Section
      title="Five cuts, in public/brand/"
      description="Open any of them in a new tab."
    >
      <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
        <Table>
          <THead>
            <TR>
              <TH>File</TH>
              <TH>Cut</TH>
              <TH>Use it for</TH>
            </TR>
          </THead>
          <TBody>
            {FILES.map(([file, cut, use]) => (
              <TR key={file}>
                <TD>
                  <ExternalTextLink
                    href={`/brand/${file}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-xs underline"
                  >
                    {file}
                  </ExternalTextLink>
                </TD>
                <TD>
                  <span className="text-xs text-fg-secondary">{cut}</span>
                </TD>
                <TD>
                  <span className="text-xs text-fg-muted">{use}</span>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </TableWrap>
      <p className="m-0 mt-4 max-w-[62ch] text-sm text-fg-secondary">
        The wordmark and the lockup carry{' '}
        <span className="font-semibold text-fg">live text, not outlines</span>. They need Inter
        to hold their fit against the drawn E — outline the text before a file goes to a
        printer or a third party. Both also default their ink to navy and flip to cream under
        the OS dark setting; on the navy sign-in field that never fires, so set{' '}
        <span className="font-mono text-xs text-fg">--emerge-ink</span> yourself there.
      </p>
    </Section>
  );
}

/* ----------------------------------------------------------------- the rules */

const DO = [
  'Set the mark in seed-orange on every ground, light or dark.',
  'Set “SEED EMERGE” as real text beside the mark, never inside the artwork.',
  'Scale the whole mark. The gap and the tilt scale with it.',
  'Reach for the mono cut when contrast is uncertain.',
];

const DONT = [
  'Reattach the arm, or level it. The gap and the tilt are the product name.',
  'Narrow the E to letter proportions inside a word. That was tried; it goes limp.',
  'Recolour any part navy. That is the cut that vanished on the sign-in screen.',
  'Add a shadow, an outline or a container. Nothing here survives 16px twice.',
];

function RulesSection() {
  return (
    <Section
      title="Four each way"
      description="What to do with the mark, and what not to."
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border p-5">
          <p className="m-0 text-sm font-semibold text-fg">Do</p>
          <ul className="m-0 mt-3 flex list-none flex-col gap-3 p-0">
            {DO.map((r) => (
              <li key={r} className="grid grid-cols-[16px_1fr] gap-2 text-sm text-fg-secondary">
                <span className="font-mono text-seed-orange">+</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-border p-5">
          <p className="m-0 text-sm font-semibold text-fg">Don’t</p>
          <ul className="m-0 mt-3 flex list-none flex-col gap-3 p-0">
            {DONT.map((r) => (
              <li key={r} className="grid grid-cols-[16px_1fr] gap-2 text-sm text-fg-muted">
                <span className="font-mono text-fg-subtle">−</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------- the page */

/*
 * Layout manager (owner 2026-10-20): each section is a movable block, per
 * person. The defaults are today's single column in today's order, so nobody
 * sees a change until they press Edit layout. Block ids are stored per user —
 * never rename one. Labels are the panels' own headings.
 */
const BRAND_LAYOUT = [
  { id: 'mark', label: 'An E with its top arm lifted off' },
  { id: 'scale', label: 'It has to survive the sidebar' },
  { id: 'construction', label: 'Ten degrees off the arm’s rest line' },
  { id: 'wordmark', label: 'The mark is the E of EMERGE' },
  { id: 'colours', label: 'Five brand colours, fixed in both themes' },
  { id: 'type', label: 'Three faces, loaded once in index.html' },
  { id: 'files', label: 'Five cuts, in public/brand/' },
  { id: 'rules', label: 'Four each way' },
] as const;

const BRAND_RENDER = {
  mark: () => <MarkSection />,
  scale: () => <ScaleSection />,
  construction: () => <ConstructionSection />,
  wordmark: () => <WordmarkSection />,
  colours: () => <ColourSection />,
  type: () => <TypeSection />,
  files: () => <FilesSection />,
  rules: () => <RulesSection />,
};

export function BrandPage() {
  const layout = usePageLayout('brand', BRAND_LAYOUT);
  return (
    <>
      <PageHeader
        eyebrow="Brand"
        title="SEED EMERGE brand"
        actions={<LayoutToolbar layout={layout} />}
      />
      <LayoutGrid layout={layout} render={BRAND_RENDER} />
    </>
  );
}
